import { ApiError, type ApiClient } from "../auth/apiClient";
import {
    QueueListResponseSchema,
    QueueNotificationStatusResponseSchema,
    QueueSummarySchema,
    QueueTimelineResponseSchema,
    QueueTreeResponseSchema,
    QueueWorkItemTalliesResponseSchema,
    UserSearchResponseSchema,
} from "./types";
import type {
    QueueListResponse,
    QueueNotificationStatusResponse,
    QueueSummary,
    QueueTimelineResponse,
    QueueTreeResponse,
    QueueWorkItemTalliesResponse,
    UserSearchResponse,
} from "./types";

// Helper kept module-local: every queue endpoint follows the same
// "throw on non-2xx with the HTTP status carried" contract, so the
// consumers (QueueAdminPage, the queue list, etc.) can branch on
// status code rather than parse free-form messages.
function throwApiError(action: string, response: Response): never {
    throw new ApiError(
        response.status,
        response.statusText,
        `${action}: ${String(response.status)} ${response.statusText}`,
    );
}

function describeLifecycleConflict(candidate: Record<string, unknown>): string | null {
    if (typeof candidate.details === "string") return candidate.details;
    if (Array.isArray(candidate.details)) {
        const details = candidate.details.filter(
            (detail): detail is string => typeof detail === "string",
        );
        return details.length > 0 ? details.join(" ") : null;
    }

    if (typeof candidate.blockers !== "object" || candidate.blockers === null) {
        return null;
    }

    const blockers = candidate.blockers as Record<string, unknown>;
    const descriptions: string[] = [];
    if (blockers.hasAwaitingWorkItems === true) {
        descriptions.push("Awaiting work items remain.");
    }
    if (Array.isArray(blockers.activeChildQueueIds) && blockers.activeChildQueueIds.length > 0) {
        descriptions.push(
            `${String(blockers.activeChildQueueIds.length)} active child queue${
                blockers.activeChildQueueIds.length === 1 ? "" : "s"
            } ${blockers.activeChildQueueIds.length === 1 ? "remains" : "remain"}.`,
        );
    }
    if (
        Array.isArray(blockers.enabledRoutingRuleIds) &&
        blockers.enabledRoutingRuleIds.length > 0
    ) {
        descriptions.push(
            `${String(blockers.enabledRoutingRuleIds.length)} enabled routing rule${
                blockers.enabledRoutingRuleIds.length === 1 ? "" : "s"
            } ${blockers.enabledRoutingRuleIds.length === 1 ? "remains" : "remain"}.`,
        );
    }
    return descriptions.length > 0 ? descriptions.join(" ") : null;
}

async function throwQueueLifecycleApiError(action: string, response: Response): Promise<never> {
    let message = `${action}: ${String(response.status)} ${response.statusText}`;
    try {
        const body: unknown = await response.json();
        if (typeof body === "object" && body !== null) {
            const candidate = body as Record<string, unknown>;
            const apiMessage =
                typeof candidate.message === "string"
                    ? candidate.message
                    : typeof candidate.title === "string"
                      ? candidate.title
                      : null;
            const details = describeLifecycleConflict(candidate);
            if (apiMessage !== null) {
                message =
                    details !== null && details.length > 0
                        ? `${apiMessage} ${details}`
                        : apiMessage;
            }
        }
    } catch {
        // Keep the status-based fallback when the error body is empty or
        // isn't JSON (for example, a proxy-generated failure).
    }

    throw new ApiError(response.status, response.statusText, message);
}

export async function fetchMyQueues(client: ApiClient): Promise<QueueListResponse> {
    const response = await client.fetch("/queues/mine");

    if (!response.ok) {
        throwApiError("Failed to fetch queues", response);
    }

    const json: unknown = await response.json();
    return QueueListResponseSchema.parse(json);
}

export async function fetchQueueTree(client: ApiClient): Promise<QueueTreeResponse> {
    const response = await client.fetch("/queues/tree");

    if (!response.ok) {
        throwApiError("Failed to fetch queue tree", response);
    }

    const json: unknown = await response.json();
    return QueueTreeResponseSchema.parse(json);
}

/**
 * Fetch per-queue Open/Pending tallies in one round trip. Caller supplies
 * an array of queue ids; the response always includes a row per requested
 * id (the server seeds zeros for queues with no matching items) so row
 * renderers never need to defensively handle missing keys.
 *
 * Returns an empty result without hitting the network when called with
 * no ids — useful for the "no queues yet" empty state, and keeps the
 * indicator query enabled-but-trivial under React Query.
 */
export async function fetchQueueWorkItemTallies(
    client: ApiClient,
    queueIds: readonly string[],
): Promise<QueueWorkItemTalliesResponse> {
    if (queueIds.length === 0) {
        return { tallies: [], asOf: new Date().toISOString() };
    }

    const params = new URLSearchParams();
    params.set("queueIds", queueIds.join(","));

    const response = await client.fetch(`/queues/counts?${params.toString()}`);

    if (!response.ok) {
        throwApiError("Failed to fetch queue tallies", response);
    }

    const json: unknown = await response.json();
    return QueueWorkItemTalliesResponseSchema.parse(json);
}

export async function fetchQueue(client: ApiClient, id: string): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(id)}`);

    if (!response.ok) {
        // Queue id is intentionally omitted from the message — it only
        // matters for diagnostics, and surfacing it in user-facing copy
        // (QueueAdminPage uses error.message as the fallback) is noise.
        throwApiError("Failed to fetch queue", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export async function fetchQueueTimeline(
    client: ApiClient,
    id: string,
): Promise<QueueTimelineResponse> {
    const response = await client.fetch(`/queues/${encodeURIComponent(id)}/timeline`);

    if (!response.ok) {
        throwApiError("Failed to fetch queue timeline", response);
    }

    const json: unknown = await response.json();
    return QueueTimelineResponseSchema.parse(json);
}

/**
 * Per-member push-mute state for one queue, for the admin member list. A
 * separate read from {@link fetchQueue} so the queue summary cache (which
 * mutations overwrite wholesale) never clobbers the mute indicators. The
 * server gates this on the same caller-relative access decision as the queue
 * read (membership, owning management-group admin, or Einstein.Admin), so it
 * inherits the same 403/404 contract.
 */
export async function fetchQueueNotificationStatus(
    client: ApiClient,
    id: string,
): Promise<QueueNotificationStatusResponse> {
    const response = await client.fetch(`/queues/${encodeURIComponent(id)}/notification-status`);

    if (!response.ok) {
        throwApiError("Failed to fetch queue notification status", response);
    }

    const json: unknown = await response.json();
    return QueueNotificationStatusResponseSchema.parse(json);
}

export interface CreateQueueInput {
    readonly name: string;
    readonly parentQueueId?: string | null;
    readonly isWorkable?: boolean;
}

export async function createQueue(
    client: ApiClient,
    input: CreateQueueInput,
): Promise<QueueSummary> {
    const body: Record<string, unknown> = { name: input.name };
    if (input.parentQueueId !== undefined && input.parentQueueId !== null) {
        body.parentQueueId = input.parentQueueId;
    }
    if (input.isWorkable !== undefined) {
        body.isWorkable = input.isWorkable;
    }

    const response = await client.fetch("/queues", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throwApiError("Failed to create queue", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export interface RenameQueueInput {
    readonly id: string;
    readonly name: string;
}

export async function renameQueue(
    client: ApiClient,
    input: RenameQueueInput,
): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(input.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: input.name }),
    });

    if (!response.ok) {
        throwApiError("Failed to rename queue", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export interface ReparentQueueInput {
    readonly id: string;
    // null promotes the queue to root.
    readonly parentQueueId: string | null;
}

export async function reparentQueue(
    client: ApiClient,
    input: ReparentQueueInput,
): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(input.id)}/parent`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parentQueueId: input.parentQueueId }),
    });

    if (!response.ok) {
        throwApiError("Failed to reparent queue", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export interface AddQueueMemberInput {
    readonly queueId: string;
    readonly userId: string;
    readonly displayName: string;
}

export async function addQueueMember(
    client: ApiClient,
    input: AddQueueMemberInput,
): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(input.queueId)}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            userId: input.userId,
            displayName: input.displayName,
        }),
    });

    if (!response.ok) {
        throwApiError("Failed to add queue member", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export interface RemoveQueueMemberInput {
    readonly queueId: string;
    readonly userId: string;
}

export async function removeQueueMember(
    client: ApiClient,
    input: RemoveQueueMemberInput,
): Promise<QueueSummary> {
    const response = await client.fetch(
        `/queues/${encodeURIComponent(input.queueId)}/members/${encodeURIComponent(input.userId)}`,
        { method: "DELETE" },
    );

    if (!response.ok) {
        throwApiError("Failed to remove queue member", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export interface SetQueueOwnerInput {
    readonly id: string;
    // null clears the owner. The backend rejects a null on a personal queue
    // implicitly (personal queues never had an owner) and rejects a value
    // pointing at a soft-deleted MG.
    readonly managementGroupId: string | null;
}

export async function setQueueOwner(
    client: ApiClient,
    input: SetQueueOwnerInput,
): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(input.id)}/owner`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ managementGroupId: input.managementGroupId }),
    });

    if (!response.ok) {
        throwApiError("Failed to set queue owner", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export async function deactivateQueue(client: ApiClient, id: string): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(id)}/deactivate`, {
        method: "POST",
    });

    if (!response.ok) {
        return throwQueueLifecycleApiError("Failed to deactivate queue", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export async function reactivateQueue(client: ApiClient, id: string): Promise<QueueSummary> {
    const response = await client.fetch(`/queues/${encodeURIComponent(id)}/reactivate`, {
        method: "POST",
    });

    if (!response.ok) {
        return throwQueueLifecycleApiError("Failed to reactivate queue", response);
    }

    const json: unknown = await response.json();
    return QueueSummarySchema.parse(json);
}

export interface UserSearchCriteria {
    readonly q?: string;
    readonly id?: string;
    readonly givenName?: string;
    readonly surname?: string;
    readonly email?: string;
}

export async function searchUsersForQueue(
    client: ApiClient,
    criteria: UserSearchCriteria,
): Promise<UserSearchResponse> {
    const params = new URLSearchParams();
    if (criteria.q !== undefined && criteria.q.length > 0) params.set("q", criteria.q);
    if (criteria.id !== undefined && criteria.id.length > 0) params.set("id", criteria.id);
    if (criteria.givenName !== undefined && criteria.givenName.length > 0) {
        params.set("givenName", criteria.givenName);
    }
    if (criteria.surname !== undefined && criteria.surname.length > 0) {
        params.set("surname", criteria.surname);
    }
    if (criteria.email !== undefined && criteria.email.length > 0) {
        params.set("email", criteria.email);
    }

    const response = await client.fetch(`/queues/users/search?${params.toString()}`);

    if (!response.ok) {
        throwApiError("Failed to search users", response);
    }

    const json: unknown = await response.json();
    return UserSearchResponseSchema.parse(json);
}
